import { useState, type CSSProperties, type ReactNode } from "react";
import { C, FONT, card, clamp, hexA } from "../theme";
import { usePumpHistory, type PumpReading } from "../lib/pumpSim";
import type { PumpInfo, Zone } from "../types";

/* One-off roles used by the station schematic / charts. Every value now resolves
   to a design token so the look matches the design AND flips correctly in dark
   mode; the nearest token is used where the design hex had no exact match. */
const D = {
  label: C.muted, driver: C.mutedDeep, stroke: C.hairline, stubOff: C.hairline, outletOff: C.hairline,
  pipeOn: C.rain, pipeOff: C.track, pipeFlowOff: C.hoverBorder, cap: C.track, filterBg: C.paper,
  screen: C.hairline, tick: C.muted, barTrack: C.track, grid1: C.track, grid2: C.raise,
  p2: C.rain, flowLine: C.sageDeep, pumpOff: C.muted,
} as const;

/** Staggered card entrance, matching the rest of the app. */
const rev = (i: number): CSSProperties => ({ animationDelay: `${i * 70}ms` });

/** Filter-load status — thresholds & labels exactly as the design. */
function filterStatus(load: number) {
  if (load < 0.4) return { label: "Clean", col: C.sage };
  if (load < 0.7) return { label: "Monitor", col: C.beacon };
  return { label: "Service due", col: C.coral };
}

/** Needle angle (deg) for a 0–3 bar dial sweeping −120°…+120° (0° = straight up). */
const needle = (p: number) => -120 + (clamp(p, 0, 3) / 3) * 240;

const eyebrow: CSSProperties = {
  fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".13em", color: D.label,
};
const eyebrowSm: CSSProperties = { ...eyebrow, fontSize: 10.5, letterSpacing: ".12em" };
const mono = "'Spline Sans Mono',ui-monospace,monospace";

/* ── KPI tile — mono figure, single colour, no leading-digit tint ──────────── */
function Stat({ label, value, unit, color, sub, subColor }: {
  label: string; value: string; unit: string; color: string; sub: string; subColor?: string;
}) {
  return (
    <div style={{ ...card, padding: "18px 20px", height: "100%" }}>
      <div style={eyebrow}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 5, marginTop: 11 }}>
        <span style={{ fontFamily: mono, fontSize: 30, fontWeight: 500, lineHeight: 0.9, color }}>{value}</span>
        <span style={{ fontSize: 13, color: D.label }}>{unit}</span>
      </div>
      <div style={{ marginTop: 9, fontSize: 12, color: subColor ?? C.textMid }}>{sub}</div>
    </div>
  );
}

/* ── A pressure dial (INLET P1 / OUTLET P2) at (cx, 86) in schematic space ─── */
function Dial({ cx, label, value, angle }: { cx: number; label: string; value: string; angle: number }) {
  const ticks = [[-20.8, 12, -17.3, 10], [-20.8, -12, -17.3, -10], [0, -25, 0, -21], [20.8, -12, 17.3, -10], [20.8, 12, 17.3, 10]];
  return (
    <>
      <line x1={cx} y1={113} x2={cx} y2={144} stroke={D.stroke} strokeWidth="2" />
      <g transform={`translate(${cx},86)`}>
        <circle r="25" fill={C.paper} stroke={D.stroke} strokeWidth="1.5" />
        {ticks.map((t, i) => <line key={i} x1={t[0]} y1={t[1]} x2={t[2]} y2={t[3]} stroke={D.tick} strokeWidth="1.4" />)}
        <g transform={`rotate(${angle})`}><line x1="0" y1="5" x2="0" y2="-19" stroke={C.seaDeep} strokeWidth="2.2" strokeLinecap="round" /></g>
        <circle r="3" fill={C.seaDeep} />
      </g>
      <text x={cx} y="42" textAnchor="middle" fontFamily={FONT.sans} fontSize="9.5" fontWeight="600" letterSpacing="1.1" fill={D.label}>{label}</text>
      <text x={cx} y="134" textAnchor="middle" fontFamily={mono} fontSize="12.5" fill={C.ink}>{value} bar</text>
    </>
  );
}

/* ── Pump & filter station schematic — exact design geometry (viewBox 940×250).
   Numbers come from `pump`/`zones`; motion is CSS-driven and pauses at standby. */
function Schematic({ pump, zones, flowTxt, p1, p2, load, active, backwashing }: {
  pump: PumpInfo; zones?: Zone[]; flowTxt: string; p1: number; p2: number; load: number; active: boolean; backwashing: boolean;
}) {
  const bodyCol = active ? C.sea : D.pumpOff;
  const pipeBase = active ? D.pipeOn : D.pipeOff;
  const pipeFlow = backwashing ? C.beacon : active ? C.ice : D.pipeFlowOff;
  const play = active ? "running" : "paused";
  const flowDur = active ? `${clamp(2.6 - pump.flow * 0.06, 0.5, 2.2).toFixed(2)}s` : "0s";
  const impDur = active ? `${clamp(1.7 - pump.flow * 0.04, 0.45, 1.4).toFixed(2)}s` : "0s";

  const dirtH = 12 + load * 64, dirtY = 194 - dirtH;
  const dirtCol = load < 0.4 ? hexA(C.sage, 0.34) : load < 0.7 ? hexA(C.beacon, 0.4) : hexA(C.coral, 0.44);

  const outlets = Array.from({ length: 7 }, (_, i) => {
    const z = zones?.[i];
    const on = z?.state === "watering";
    const col = on ? (z!.type === "drip" ? C.sage : C.sea) : D.outletOff;
    const stub = on ? (z!.type === "drip" ? C.sage : C.sea) : D.stubOff;
    return { y: 104 + i * 14, col, stub };
  });

  const lbl = (x: number, y: number, s: string, ls = "1.2") => (
    <text x={x} y={y} textAnchor="middle" fontFamily={FONT.sans} fontSize="9.5" fontWeight="600" letterSpacing={ls} fill={D.label}>{s}</text>
  );

  return (
    <svg viewBox="0 0 940 250" style={{ width: "100%", height: "auto", display: "block" }}>
      {/* pipe + travelling beads */}
      <path d="M88 150 H792" stroke={pipeBase} strokeWidth="13" fill="none" strokeLinecap="round" />
      <path d="M88 150 H792" stroke={pipeFlow} strokeWidth="5" fill="none" strokeLinecap="round" strokeDasharray="3 17"
        style={{ animation: `flowDash ${flowDur} linear infinite ${play}` }} />

      {/* well */}
      <rect x="36" y="116" width="52" height="68" rx="11" fill={C.paper} stroke={D.stroke} strokeWidth="1.5" />
      <rect x="41" y="150" width="42" height="29" rx="6" fill={hexA(C.ice, 0.3)} />
      {lbl(62, 205, "WELL")}

      {/* pump */}
      <circle cx="150" cy="150" r="30" fill={bodyCol} opacity="0.10" />
      <circle cx="150" cy="150" r="30" fill={C.paper} stroke={bodyCol} strokeWidth="2.5" />
      <g style={{ transformOrigin: "150px 150px", animation: `spin ${impDur} linear infinite ${play}` }}>
        {[[150, 130], [167.3, 140], [167.3, 160], [150, 170], [132.7, 160], [132.7, 140]].map(([x, y], i) => (
          <line key={i} x1="150" y1="150" x2={x} y2={y} stroke={bodyCol} strokeWidth="3.4" strokeLinecap="round" />
        ))}
        <circle cx="150" cy="150" r="5.5" fill={bodyCol} />
      </g>
      {lbl(150, 205, "PUMP")}

      {/* screen filter */}
      <rect x="320" y="98" width="88" height="100" rx="14" fill={C.paper} stroke={D.stroke} strokeWidth="1.5" />
      <rect x="333" y="90" width="62" height="13" rx="6" fill={D.cap} stroke={D.stroke} />
      <clipPath id="filtclip"><rect x="328" y="106" width="72" height="88" rx="9" /></clipPath>
      <g clipPath="url(#filtclip)">
        <rect x="328" y="106" width="72" height="88" fill={D.filterBg} />
        {[340, 352, 364, 376, 388].map((x) => <line key={x} x1={x} y1="106" x2={x} y2="194" stroke={D.screen} strokeWidth="1.4" />)}
        <rect x="328" y={dirtY} width="72" height={dirtH} fill={dirtCol} />
      </g>
      {lbl(364, 222, "SCREEN FILTER")}

      {/* flow meter */}
      <rect x="545" y="118" width="150" height="64" rx="13" fill={C.paper} stroke={D.stroke} strokeWidth="1.5" />
      <text x="558" y="152" fontFamily={mono} fontWeight="500" fontSize="25" fill={C.seaDeep}>{flowTxt}</text>
      <text x="558" y="170" fontFamily={FONT.sans} fontSize="10" fill={D.label}>L / min</text>
      <g style={{ transformOrigin: "668px 150px", animation: `spin ${flowDur} linear infinite ${play}` }}>
        <circle cx="668" cy="150" r="12" fill="none" stroke={C.ice} strokeWidth="1.4" opacity="0.5" />
        {[[668, 140], [676.7, 155], [659.3, 155]].map(([x, y], i) => (
          <line key={i} x1="668" y1="150" x2={x} y2={y} stroke={C.ice} strokeWidth="2" strokeLinecap="round" />
        ))}
      </g>
      {lbl(620, 108, "FLOW METER")}

      {/* 7-zone manifold */}
      <rect x="790" y="100" width="16" height="104" rx="8" fill={D.cap} stroke={D.stroke} />
      {outlets.map((o, i) => (
        <g key={i}>
          <line x1="806" y1={o.y} x2="832" y2={o.y} stroke={o.stub} strokeWidth="3" strokeLinecap="round" />
          <circle cx="839" cy={o.y} r="4" fill={o.col} />
        </g>
      ))}
      {lbl(828, 222, "7 ZONES")}

      {/* gauges */}
      <Dial cx={250} label="INLET P1" value={p1.toFixed(2)} angle={needle(p1)} />
      <Dial cx={470} label="OUTLET P2" value={p2.toFixed(2)} angle={needle(p2)} />
    </svg>
  );
}

/* ── Filter-condition card with the backwash control ────────────────────────
   The controller exposes NO backwash endpoint yet, so this button is a local
   PREVIEW only: it animates the schematic for ~3.4s but does not flush anything
   and must never claim a real backwash occurred. When a real POST
   /api/pump/backwash exists, drop the "(simulated)" labelling. ─────────────── */
function FilterCondition({ dP, load, onBackwash, backwashing }: {
  dP: string; load: number; onBackwash: () => void; backwashing: boolean;
}) {
  const fs = filterStatus(load);
  const pct = `${(load * 100).toFixed(0)}%`;
  return (
    <div style={{ ...card, borderRadius: 20, padding: "22px 24px", height: "100%" }}>
      <div style={{ fontFamily: FONT.serif, fontSize: 18, color: C.ink }}>Filter condition</div>
      <p style={{ margin: "5px 0 18px", fontSize: 12, color: D.label, maxWidth: "46ch" }}>
        Pressure drop across the screen rises as it loads with sand and organics. A backwash reverse-flushes it back to a clean baseline.
      </p>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
        <div>
          <div style={{ fontFamily: mono, fontSize: 34, lineHeight: 0.9, color: fs.col }}>{dP}</div>
          <div style={{ marginTop: 6, fontSize: 11.5, color: D.label }}>ΔP across filter · bar</div>
        </div>
        <span style={{
          display: "inline-flex", alignItems: "center", gap: 7, padding: "5px 13px", borderRadius: 99,
          fontSize: 12, fontWeight: 600, color: fs.col, background: hexA(fs.col, 0.12), border: `1px solid ${hexA(fs.col, 0.34)}`,
        }}>{fs.label}</span>
      </div>
      <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", fontSize: 11, color: D.label }}>
        <span>clean</span><span>filter load</span><span>service</span>
      </div>
      <div style={{ marginTop: 6, height: 9, borderRadius: 99, background: D.barTrack, overflow: "hidden" }}>
        <div style={{ height: "100%", width: pct, background: fs.col, transition: "width .4s" }} />
      </div>
      <button
        onClick={onBackwash}
        disabled={backwashing}
        aria-label={backwashing ? "Simulating backwash (preview only)" : "Run backwash (simulated — not wired to controller)"}
        style={{
          marginTop: 20, width: "100%", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
          padding: 12, borderRadius: 13, fontSize: 13.5, fontWeight: 600, fontFamily: "inherit",
          cursor: backwashing ? "not-allowed" : "pointer",
          color: backwashing ? C.textLow : C.sea,
          background: backwashing ? C.track : hexA(C.sea, 0.08),
          border: `1px solid ${backwashing ? C.hairline : hexA(C.sea, 0.4)}`,
        }}
      >
        <svg className={backwashing ? "spin" : undefined} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" />
        </svg>
        {backwashing ? "Simulating backwash…" : "Run backwash (simulated)"}
      </button>
      <div style={{ marginTop: 8, fontSize: 11, lineHeight: 1.4, color: D.label, textAlign: "center" }}>
        {backwashing
          ? "Simulated — no controller connected. Nothing is being flushed."
          : "Preview only — not wired to the controller. This will not flush the filter."}
      </div>
    </div>
  );
}

/* ── Live telemetry — two stacked traces from real polled history ──────────── */
function Telemetry({ hist }: { hist: PumpReading[] }) {
  const W = 320, H = 76, n = hist.length, ready = n > 1;
  const sx = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * W);
  const pv = hist.flatMap((h) => [h.p1, h.p2]);
  const pmin = pv.length ? Math.min(...pv) : 0, pmax = pv.length ? Math.max(...pv) : 1;
  const span = Math.max(0.8, pmax - pmin), plo = pmin - span * 0.5, phi = pmax + span * 0.5;
  const pY = (v: number) => (66 - ((v - plo) / (phi - plo)) * 56).toFixed(1);
  const fmax = Math.max(12, ...hist.map((h) => h.flow));
  const fY = (v: number) => (66 - (clamp(v, 0, fmax) / fmax) * 56).toFixed(1);
  const pts = (key: "p1" | "p2" | "flow", y: (v: number) => string) => hist.map((h, i) => `${sx(i).toFixed(1)},${y(h[key])}`).join(" ");
  const flowPts = pts("flow", fY);

  const legend = (label: string, col: string) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 12, height: 2.5, borderRadius: 2, background: col }} />{label}
    </span>
  );
  const Chart = ({ children }: { children: ReactNode }) =>
    ready ? <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block" }}>{children}</svg>
      : <div style={{ height: H, display: "flex", alignItems: "center", fontSize: 12, color: D.label }}>Collecting telemetry…</div>;

  return (
    <div style={{ ...card, borderRadius: 20, padding: "22px 24px", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <span style={{ fontFamily: FONT.serif, fontSize: 18, color: C.ink }}>Live telemetry</span>
        <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 11.5, color: C.textMid }}>
          {legend("P1", C.sea)}{legend("P2", D.p2)}
        </div>
      </div>
      <div style={{ ...eyebrowSm, marginBottom: 4 }}>Pressure · bar</div>
      <Chart>
        <line x1="0" y1="66" x2={W} y2="66" stroke={D.grid1} />
        <line x1="0" y1="38" x2={W} y2="38" stroke={D.grid2} />
        <polyline points={pts("p2", pY)} fill="none" stroke={D.p2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <polyline points={pts("p1", pY)} fill="none" stroke={C.sea} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </Chart>
      <div style={{ ...eyebrowSm, margin: "14px 0 4px" }}>Flow · L/min</div>
      <Chart>
        <defs>
          <linearGradient id="flowfill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={C.sage} stopOpacity="0.26" /><stop offset="1" stopColor={C.sage} stopOpacity="0" />
          </linearGradient>
        </defs>
        <line x1="0" y1="66" x2={W} y2="66" stroke={D.grid1} />
        <polygon points={`0,68 ${flowPts} ${W},68`} fill="url(#flowfill)" />
        <polyline points={flowPts} fill="none" stroke={D.flowLine} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </Chart>
    </div>
  );
}

/* ── Pump page — all numbers from state.pump / state.zones (worker / HA) ────── */
export function Pump({ pump, zones }: { pump?: PumpInfo; paused?: boolean; source?: string; zones?: Zone[] }) {
  const history = usePumpHistory(pump);
  const [backwashing, setBackwashing] = useState(false);

  if (!pump) {
    return (
      <div className="card-in" style={{ ...card, padding: 24, fontSize: 13, color: C.textMid }}>
        Pump telemetry isn’t in this snapshot yet — it appears once the controller reports.
      </div>
    );
  }

  const p1 = pump.p1, p2 = pump.p2, dP = Math.max(0, p1 - p2);
  const load = clamp(pump.clog, 0, 1);
  const fs = filterStatus(load);
  const flowTxt = pump.flow.toFixed(1);
  const active = pump.on || pump.flow > 0.3 || backwashing;

  const driver = zones?.find((z) => z.state === "watering");
  const pumpDriver = driver ? `${driver.name} · ${driver.type === "drip" ? "drip" : "sprinkler"}` : "No zone calling — line holding pressure";
  const stat = backwashing ? { l: "Backwash (sim)", c: C.ice } : active ? { l: "Running", c: C.sea } : { l: "Standby", c: C.textLow };

  // Simulation only: animates the station for ~3.4s. No backwash endpoint exists
  // on the controller yet, so nothing is actually flushed. Replace with a real
  // POST /api/pump/backwash call (and honest success/failure copy) once wired.
  const runBackwash = () => {
    if (backwashing) return;
    setBackwashing(true);
    setTimeout(() => setBackwashing(false), 3400);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <style>{`
        @media (min-width:560px){.marram-pump-kpis{grid-template-columns:repeat(2,1fr) !important}}
        @media (min-width:1040px){.marram-pump-kpis{grid-template-columns:repeat(4,1fr) !important}}
        @media (min-width:900px){.marram-pump-lower{grid-template-columns:1fr 1fr !important}}
      `}</style>

      {/* KPI row */}
      <div className="marram-pump-kpis" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 18 }}>
        <div className="card-in" style={rev(0)}><Stat label="Flow rate" value={flowTxt} unit="L/min" color={C.seaDeep} sub="turbine meter · live" /></div>
        <div className="card-in" style={rev(1)}><Stat label="Inlet pressure" value={p1.toFixed(2)} unit="bar" color={C.ink} sub="P1 · before filter" /></div>
        <div className="card-in" style={rev(2)}><Stat label="Outlet pressure" value={p2.toFixed(2)} unit="bar" color={C.ink} sub="P2 · after filter" /></div>
        <div className="card-in" style={rev(3)}><Stat label="Differential ΔP" value={dP.toFixed(2)} unit="bar" color={fs.col} sub={`filter · ${fs.label}`} subColor={fs.col} /></div>
      </div>

      {/* Station schematic */}
      <div className="card-in" style={{ ...card, borderRadius: 20, padding: "22px 24px", ...rev(4) }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 6 }}>
          <span style={{ fontFamily: FONT.serif, fontSize: 18, color: C.ink }}>Pump &amp; filter station</span>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 12, color: D.driver }}>{pumpDriver}</span>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 7, padding: "4px 12px", borderRadius: 99,
              fontSize: 11, fontWeight: 600, color: stat.c, background: hexA(stat.c, 0.12), border: `1px solid ${hexA(stat.c, 0.34)}`,
            }}>
              <span className={active ? "dot-pulse" : undefined} style={{ width: 6, height: 6, borderRadius: 9, background: stat.c }} />
              {stat.l}
            </span>
          </div>
        </div>
        <Schematic pump={pump} zones={zones} flowTxt={flowTxt} p1={p1} p2={p2} load={load} active={active} backwashing={backwashing} />
        <div style={{ marginTop: 6, fontSize: 11.5, color: D.label }}>
          Water is drawn from the well, pressurised by the pump, cleaned through the screen filter, metered, then split across the seven zones — one at a time to hold line pressure.
        </div>
      </div>

      {/* Filter condition + live telemetry */}
      <div className="marram-pump-lower" style={{ display: "grid", gridTemplateColumns: "1fr", gap: 22, alignItems: "start" }}>
        <div className="card-in" style={rev(5)}><FilterCondition dP={dP.toFixed(2)} load={load} onBackwash={runBackwash} backwashing={backwashing} /></div>
        <div className="card-in" style={rev(6)}><Telemetry hist={history} /></div>
      </div>
    </div>
  );
}
